/**
 * SuperHeadlinerHero — "This Week's Call" dynamic hero card.
 *
 * Lives on /widget as the top visual. Reads GET /api/widget/headliner,
 * picks the dominant story from the rules engine, and renders it as a
 * centered, tinted hero card. Replaces the static "Today's Call" when
 * a real signal is firing; falls back gracefully on quiet weeks.
 *
 * Tints map to tailwind accent colors keyed off the rule that fired
 * (rose=urgent, amber=act soon, emerald=opportunity/positive,
 *  sky=weather-driven, violet=disease).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

const TINT = {
  rose:    { border: 'border-rose-500/40',    bg: 'bg-rose-950/25',    label: 'text-rose-300',    action: 'text-rose-200' },
  amber:   { border: 'border-amber-500/40',   bg: 'bg-amber-950/25',   label: 'text-amber-300',   action: 'text-amber-200' },
  emerald: { border: 'border-emerald-500/40', bg: 'bg-emerald-950/25', label: 'text-emerald-300', action: 'text-emerald-200' },
  sky:     { border: 'border-sky-500/40',     bg: 'bg-sky-950/25',     label: 'text-sky-300',     action: 'text-sky-200' },
  violet:  { border: 'border-violet-500/40',  bg: 'bg-violet-950/25',  label: 'text-violet-300',  action: 'text-violet-200' },
};

export function SuperHeadlinerHero() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/widget/headliner`, { withCredentials: true });
      setData(r.data?.headline || null);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 10 * 60 * 1000); // refresh every 10 min
    return () => clearInterval(id);
  }, [load]);

  if (!data) return null;
  const t = TINT[data.tint] || TINT.emerald;

  return (
    <div
      data-testid="widget-headliner-hero"
      className={`rounded-2xl border ${t.border} ${t.bg} backdrop-blur px-6 py-5 sm:px-8 sm:py-6 text-center`}
    >
      <div className={`text-[10px] uppercase tracking-[0.35em] font-bold ${t.label} mb-2 flex items-center justify-center gap-2`}>
        · This Week's Call ·
      </div>
      <div className="text-4xl sm:text-5xl mb-2" aria-hidden="true">{data.icon}</div>
      <h2 className="text-2xl sm:text-3xl font-bold text-zinc-100 leading-tight max-w-2xl mx-auto">
        {data.headline}
      </h2>
      <p className="mt-3 text-sm sm:text-base text-zinc-400 leading-relaxed max-w-2xl mx-auto">
        {data.subhead}
      </p>
      <div className="mt-4 inline-block rounded-lg border border-zinc-800/80 bg-black/30 px-4 py-2 font-mono text-xs sm:text-sm tabular-nums text-zinc-200">
        {data.metric}
      </div>
      <div className={`mt-4 text-sm sm:text-base font-semibold ${t.action} max-w-xl mx-auto`}>
        → {data.action}
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-widest text-zinc-600">
        {data.source} · confidence {Math.round((data.confidence || 0) * 100)}%
      </div>
    </div>
  );
}

export default SuperHeadlinerHero;
